import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import {
  Zap, Plus, Play, Pause, Trash2, X, ChevronRight, ChevronDown, Loader2,
  Mail, ListTodo, Bell, GitBranch, Webhook, RefreshCw, CheckCircle2, AlertCircle, Settings as SettingsIcon,
} from "lucide-react";

const ACTION_ICONS = {
  send_email: Mail,
  create_task: ListTodo,
  update_lead_status: GitBranch,
  send_notification: Bell,
  webhook: Webhook,
};

const TRIGGER_DESCRIPTIONS = {
  "lead.created": "Runs whenever a new lead is added to the CRM",
  "lead.status_changed": "Runs when a lead's status moves (e.g., new → qualified)",
  "deal.created": "Runs whenever a new deal is added to a pipeline",
  "deal.stage_changed": "Runs when a deal moves between pipeline stages",
  "sale.completed": "Runs after any completed sale (POS, Online, Create Sale)",
  "product.low_stock": "Runs when a product crosses its low-stock threshold",
  "inbox.message_received": "Runs when an inbound conversation message arrives",
};

export default function WorkflowsPage() {
  const { subdomain } = useParams();
  const [workflows, setWorkflows] = useState([]);
  const [catalog, setCatalog] = useState({ triggers: {}, actions: {} });
  const [planLocked, setPlanLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("workflows");
  const [runs, setRuns] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([
        api.get("/t/workflows/catalog"),
        api.get("/t/workflows"),
      ]);
      setCatalog(c.data);
      setWorkflows(w.data || []);
      setPlanLocked(false);
    } catch (e) {
      if (e?.response?.status === 403) setPlanLocked(true);
    } finally { setLoading(false); }
  };
  const loadRuns = async () => {
    try { const r = await api.get("/t/workflows-runs"); setRuns(r.data || []); } catch { setRuns([]); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "runs") loadRuns(); }, [tab]);

  const toggle = async (w) => {
    try { await api.patch(`/t/workflows/${w.id}`, { enabled: !w.enabled }); toast.success(w.enabled ? "Paused" : "Activated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (w) => {
    if (!window.confirm(`Delete workflow "${w.name}"?`)) return;
    try { await api.delete(`/t/workflows/${w.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const test = async (w) => {
    try {
      // Build a sample payload based on trigger
      const samples = {
        "lead.created": { id: "sample-lead", name: "Sample Lead", email: "sample@example.com", company: "ACME", status: "new" },
        "lead.status_changed": { id: "sample-lead", lead_id: "sample-lead", name: "Sample Lead", status: "qualified", previous_status: "new" },
        "deal.created": { id: "sample-deal", title: "Sample Deal", value: 50000, stage: "discovery" },
        "deal.stage_changed": { id: "sample-deal", deal_id: "sample-deal", title: "Sample Deal", stage: "won", previous_stage: "negotiation" },
        "sale.completed": { reference: "SALE-TEST", total: 25000, customer_name: "Sample Customer" },
        "product.low_stock": { name: "Sample Product", stock: 2 },
        "inbox.message_received": { customer_name: "Sample Customer", body: "Hi there", channel_type: "whatsapp" },
      };
      await api.post(`/t/workflows/${w.id}/test`, samples[w.trigger] || {});
      toast.success("Test run dispatched");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (planLocked) {
    return (
      <div className="p-8" data-testid="wf-plan-locked">
        <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <Zap size={32} className="mx-auto text-amber-700 mb-3" />
          <div className="font-display font-extrabold text-xl text-amber-900">Workflow Automation is not in your plan</div>
          <Link to={`/t/${subdomain}/billing`} className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-[#1FA868] text-white font-bold text-sm">View plans</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="workflows-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-lg shadow-amber-500/30">
            <Zap size={26} />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-2xl lg:text-3xl text-black">Workflow Automation</h1>
            <p className="text-sm text-black/55 mt-1">Automate the boring stuff. When X happens, do Y — without writing code.</p>
          </div>
        </div>
        <button onClick={() => setEditing({ _new: true })} className="px-5 py-2.5 rounded-xl bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="wf-new-btn">
          <Plus size={14} /> New Workflow
        </button>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-black/8">
        {[{ key: "workflows", lbl: "Workflows" }, { key: "runs", lbl: "Run History" }].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`wf-tab-${t.key}`}
            className={`px-4 py-3 text-sm font-bold border-b-2 ${tab === t.key ? "border-amber-500 text-amber-600" : "border-transparent text-black/55 hover:text-black"}`}>
            {t.lbl}
          </button>
        ))}
      </div>

      {tab === "workflows" ? (
        loading ? <Loader /> : workflows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/8 p-12 text-center" data-testid="wf-empty">
            <Zap size={36} className="mx-auto text-black/25 mb-3" />
            <div className="font-bold text-black/70">No automations yet</div>
            <p className="text-sm text-black/55 mt-1 max-w-md mx-auto">Create your first workflow to auto-respond to leads, send follow-ups, alert on low stock, and more.</p>
            <button onClick={() => setEditing({ _new: true })} className="mt-4 px-5 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-bold">Create your first workflow</button>
          </div>
        ) : (
          <div className="space-y-3">
            {workflows.map((w) => (
              <WorkflowRow key={w.id} w={w} catalog={catalog} onToggle={() => toggle(w)} onEdit={() => setEditing(w)} onRemove={() => remove(w)} onTest={() => test(w)} />
            ))}
          </div>
        )
      ) : (
        loading ? <Loader /> : <RunsView runs={runs} />
      )}

      {editing && <WorkflowEditor wf={editing} catalog={catalog} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function WorkflowRow({ w, catalog, onToggle, onEdit, onRemove, onTest }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`bg-white rounded-2xl border border-black/8 overflow-hidden ${w.enabled ? "" : "opacity-65"}`} data-testid={`wf-row-${w.id}`}>
      <div className="p-4 flex items-center gap-3">
        <button onClick={() => setExpanded(!expanded)} className="text-black/55"><ChevronRight size={16} className={`transition ${expanded ? "rotate-90" : ""}`} /></button>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${w.enabled ? "bg-amber-100 text-amber-700" : "bg-black/5 text-black/45"}`}><Zap size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-black truncate flex items-center gap-2">
            {w.name}
            {!w.enabled && <span className="text-[10px] uppercase font-bold bg-black/8 text-black/55 px-1.5 py-0.5 rounded">Paused</span>}
          </div>
          <div className="text-xs text-black/55 mt-0.5 flex items-center gap-3 flex-wrap">
            <span className="bg-violet-50 text-violet-700 font-semibold px-2 py-0.5 rounded">{catalog.triggers?.[w.trigger] || w.trigger}</span>
            <span>{w.actions?.length || 0} action{w.actions?.length === 1 ? "" : "s"}</span>
            {w.run_count > 0 && <span className="text-emerald-700 font-bold">{w.run_count} runs</span>}
            {w.last_run_at && <span>· last: {new Date(w.last_run_at).toLocaleString()}</span>}
          </div>
        </div>
        <button onClick={onTest} className="px-3 py-1.5 rounded-lg bg-black/5 text-xs font-bold hover:bg-black/10" data-testid={`wf-test-${w.id}`}>Test</button>
        <button onClick={onToggle} className={`p-2 rounded-lg ${w.enabled ? "bg-emerald-50 text-emerald-700" : "bg-black/5 text-black/55"}`} data-testid={`wf-toggle-${w.id}`}>
          {w.enabled ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <button onClick={onEdit} className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#1FA868] hover:bg-[#1FA868]/10" data-testid={`wf-edit-${w.id}`}>Edit</button>
        <button onClick={onRemove} className="p-2 rounded-lg text-red-600 hover:bg-red-50" data-testid={`wf-delete-${w.id}`}><Trash2 size={13} /></button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pl-16 border-t border-black/5 pt-3 bg-black/[0.015]">
          {w.description && <div className="text-sm text-black/65 mb-3">{w.description}</div>}
          <div className="text-[10px] uppercase font-bold text-black/45 mb-2">Steps</div>
          <div className="space-y-2">
            {(w.actions || []).map((a, i) => {
              const Icon = ACTION_ICONS[a.type] || GitBranch;
              return (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-7 h-7 rounded-md bg-white border border-black/10 flex items-center justify-center text-black/55"><Icon size={13} /></div>
                  <div className="flex-1">
                    <span className="font-bold text-black/85">{catalog.actions?.[a.type] || a.type}</span>
                    {a.to && <span className="text-black/55 ml-2">→ {a.to}</span>}
                    {a.title && <span className="text-black/55 ml-2">"{a.title}"</span>}
                    {a.status && <span className="text-black/55 ml-2">→ {a.status}</span>}
                    {a.url && <span className="text-black/55 ml-2 truncate">→ {a.url}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function WorkflowEditor({ wf, catalog, onClose, onSaved }) {
  const isNew = wf._new;
  const [f, setF] = useState({
    name: wf.name || "",
    description: wf.description || "",
    enabled: wf.enabled !== false,
    trigger: wf.trigger || "lead.created",
    filters: wf.filters || {},
    actions: wf.actions || [{ type: "send_email", to: "", subject: "", body: "" }],
  });
  const [saving, setSaving] = useState(false);

  const addAction = () => setF({ ...f, actions: [...f.actions, { type: "create_task", title: "" }] });
  const removeAction = (i) => setF({ ...f, actions: f.actions.filter((_, idx) => idx !== i) });
  const updateAction = (i, patch) => setF({ ...f, actions: f.actions.map((a, idx) => idx === i ? { ...a, ...patch } : a) });

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    if (f.actions.length === 0) return toast.error("Add at least one action");
    setSaving(true);
    try {
      if (isNew) await api.post("/t/workflows", f);
      else await api.patch(`/t/workflows/${wf.id}`, f);
      toast.success(isNew ? "Workflow created" : "Updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="wf-editor">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="font-display font-extrabold text-lg text-black">{isNew ? "New Workflow" : "Edit Workflow"}</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field lbl="Workflow name">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g., Auto-reply to new leads" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="wf-name" />
          </Field>
          <Field lbl="Description (optional)">
            <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="wf-description" />
          </Field>

          {/* Trigger */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
            <div className="text-[10px] uppercase font-bold text-violet-700 mb-2">When this happens (trigger)</div>
            <select value={f.trigger} onChange={(e) => setF({ ...f, trigger: e.target.value })} className="w-full border border-violet-300 rounded-lg px-3 py-2 text-sm font-bold bg-white" data-testid="wf-trigger">
              {Object.entries(catalog.triggers).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <p className="text-xs text-violet-700/80 mt-2">{TRIGGER_DESCRIPTIONS[f.trigger]}</p>
          </div>

          {/* Actions */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase font-bold text-amber-800">Then do this (actions)</div>
              <button onClick={addAction} className="text-xs font-bold text-amber-800 hover:underline" data-testid="wf-add-action">+ Add action</button>
            </div>
            <div className="space-y-3">
              {f.actions.map((a, i) => (
                <ActionBlock key={i} action={a} index={i} catalog={catalog} onChange={(p) => updateAction(i, p)} onRemove={() => removeAction(i)} />
              ))}
            </div>
            <div className="mt-3 text-[11px] text-amber-800/80 bg-amber-100/60 rounded-md p-2">
              💡 Use <code className="bg-white px-1 rounded font-mono">{`{{ field }}`}</code> placeholders to interpolate trigger payload values, e.g., <code className="bg-white px-1 rounded font-mono">{`{{ name }}`}</code> or <code className="bg-white px-1 rounded font-mono">{`{{ customer_name }}`}</code>.
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-black/5 text-sm font-bold text-black/70">Cancel</button>
            <button onClick={submit} disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-50" data-testid="wf-save">
              {saving ? "Saving…" : "Save workflow"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionBlock({ action, index, catalog, onChange, onRemove }) {
  const Icon = ACTION_ICONS[action.type] || GitBranch;
  return (
    <div className="bg-white rounded-lg border border-amber-200 p-3" data-testid={`wf-action-${index}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center"><Icon size={14} /></div>
        <select value={action.type} onChange={(e) => onChange({ type: e.target.value })} className="flex-1 border border-black/15 rounded-md px-2 py-1.5 text-sm font-bold" data-testid={`wf-action-type-${index}`}>
          {Object.entries(catalog.actions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={onRemove} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md"><X size={14} /></button>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {action.type === "send_email" && (
          <>
            <Mini lbl="To"><input value={action.to || ""} onChange={(e) => onChange({ to: e.target.value })} placeholder="{{ email }} or fixed address" data-testid={`wf-action-to-${index}`} /></Mini>
            <Mini lbl="Subject"><input value={action.subject || ""} onChange={(e) => onChange({ subject: e.target.value })} placeholder="Welcome {{ name }}!" /></Mini>
            <Mini lbl="Body"><textarea value={action.body || ""} onChange={(e) => onChange({ body: e.target.value })} rows={3} placeholder="<p>Hi {{ name }}, thanks for reaching out…</p>" /></Mini>
          </>
        )}
        {action.type === "create_task" && (
          <>
            <Mini lbl="Title"><input value={action.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="Follow up with {{ name }}" data-testid={`wf-action-title-${index}`} /></Mini>
            <Mini lbl="Priority">
              <select value={action.priority || "normal"} onChange={(e) => onChange({ priority: e.target.value })}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </Mini>
          </>
        )}
        {action.type === "update_lead_status" && (
          <Mini lbl="New status">
            <select value={action.status || "contacted"} onChange={(e) => onChange({ status: e.target.value })} data-testid={`wf-action-status-${index}`}>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="unqualified">Unqualified</option>
              <option value="lost">Lost</option>
            </select>
          </Mini>
        )}
        {action.type === "send_notification" && (
          <>
            <Mini lbl="Title"><input value={action.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="Low stock alert" /></Mini>
            <Mini lbl="Body"><input value={action.body || ""} onChange={(e) => onChange({ body: e.target.value })} placeholder="{{ name }} has only {{ stock }} left" /></Mini>
          </>
        )}
        {action.type === "webhook" && (
          <Mini lbl="POST URL"><input value={action.url || ""} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://hooks.zapier.com/..." data-testid={`wf-action-url-${index}`} /></Mini>
        )}
      </div>
    </div>
  );
}

function RunsView({ runs }) {
  if (runs.length === 0) return (
    <div className="bg-white rounded-2xl border border-black/8 p-12 text-center" data-testid="wf-runs-empty">
      <RefreshCw size={28} className="mx-auto text-black/25 mb-2" />
      <div className="font-bold text-black/70">No runs yet</div>
    </div>
  );
  return (
    <div className="bg-white rounded-2xl border border-black/8 divide-y divide-black/5" data-testid="wf-runs-list">
      {runs.map((r) => (
        <div key={r.id} className="p-4 flex items-center gap-3" data-testid={`wf-run-${r.id}`}>
          {r.status === "success" ? <CheckCircle2 size={18} className="text-emerald-600" /> : <AlertCircle size={18} className="text-amber-600" />}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-black">{r.workflow_name} <span className="text-black/45 font-normal">· {r.trigger}</span></div>
            <div className="text-xs text-black/55 mt-0.5">{r.actions?.length || 0} action{r.actions?.length === 1 ? "" : "s"} · {r.actions?.filter((a) => a.ok).length || 0} ok · {new Date(r.started_at).toLocaleString()}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ lbl, children }) {
  return <div><label className="text-[11px] uppercase tracking-wide font-bold text-black/55">{lbl}</label><div className="mt-1">{children}</div></div>;
}
function Mini({ lbl, children }) {
  return <div><div className="text-[10px] uppercase font-bold text-black/45 mb-1">{lbl}</div>{withProps(children, "w-full border border-black/15 rounded-md px-2 py-1.5 text-sm")}</div>;
}
function withProps(child, className) {
  if (!child) return null;
  return { ...child, props: { ...child.props, className: (child.props.className || "") + " " + className } };
}
function Loader() { return <div className="p-12 flex justify-center text-black/45"><Loader2 className="animate-spin" /></div>; }
